// components/ManageAccountsModal.jsx
import { useState, useEffect } from 'react';
import axios from 'axios';
import API_BASE_PATH from '../apiConfig';
import { formatCurrency } from '../utils';
import { showToast } from './Toast';

const ManageAccountsModal = ({ onClose, onSave }) => {
  const [accounts, setAccounts] = useState([]);
  const [debts, setDebts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showAddForm, setShowAddForm] = useState(false);
  const [editingAccount, setEditingAccount] = useState(null);
  const [formData, setFormData] = useState({
    name: '',
    type: 'checking',
    institution: '',
    account_number_last4: '',
    current_balance: '',
    is_active: true,
    notes: ''
  });

  useEffect(() => {
    fetchAccounts();
  }, []);

  const fetchAccounts = async () => {
    setLoading(true);
    try {
      const [accountsRes, debtsRes] = await Promise.all([
        axios.get(`${API_BASE_PATH}/accounts`),
        axios.get(`${API_BASE_PATH}/debts`)
      ]);
      setAccounts(accountsRes.data || []);
      setDebts(debtsRes.data || []);
    } catch (error) {
      console.error('Error fetching accounts:', error);
      showToast('Failed to load accounts', 'error');
    } finally {
      setLoading(false);
    }
  };

  const handleChange = (e) => {
    const { name, value, type, checked } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: type === 'checkbox' ? checked : value
    }));
  };

  const handleAddAccount = () => {
    setEditingAccount(null);
    setFormData({
      name: '',
      type: 'checking',
      institution: '',
      account_number_last4: '',
      current_balance: '',
      is_active: true,
      notes: ''
    });
    setShowAddForm(true);
  };

  const handleEditAccount = (account) => {
    setEditingAccount(account);
    setFormData({
      name: account.name,
      type: account.type,
      institution: account.institution || '',
      account_number_last4: account.account_number_last4 || '',
      current_balance: account.current_balance?.toString() || '',
      is_active: account.is_active !== undefined ? account.is_active : true,
      notes: account.notes || ''
    });
    setShowAddForm(true);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    if (!formData.name || !formData.type) {
      showToast('Please fill in required fields', 'error');
      return;
    }

    try {
      const payload = {
        name: formData.name.trim(),
        type: formData.type,
        institution: formData.institution.trim() || null,
        account_number_last4: formData.account_number_last4.trim() || null,
        current_balance: formData.current_balance ? parseFloat(formData.current_balance) : 0,
        is_active: formData.is_active,
        notes: formData.notes.trim() || null
      };

      if (editingAccount) {
        await axios.put(`${API_BASE_PATH}/accounts/${editingAccount.id}`, payload);
        showToast('Account updated successfully', 'success');
      } else {
        await axios.post(`${API_BASE_PATH}/accounts`, payload);
        showToast('Account created successfully', 'success');
      }

      setShowAddForm(false);
      setEditingAccount(null);
      fetchAccounts();
    } catch (error) {
      console.error('Error saving account:', error);
      showToast('Failed to save account', 'error');
    }
  };

  const handleDeleteAccount = async (id) => {
    if (!confirm('Delete this account? This will not delete associated transactions.')) return;

    try {
      await axios.delete(`${API_BASE_PATH}/accounts/${id}`);
      showToast('Account deleted successfully', 'success');
      fetchAccounts();
    } catch (error) {
      console.error('Error deleting account:', error);
      showToast('Failed to delete account', 'error');
    }
  };

  const handleToggleActive = async (account) => {
    try {
      await axios.put(`${API_BASE_PATH}/accounts/${account.id}`, {
        is_active: !account.is_active
      });
      showToast(`Account ${!account.is_active ? 'activated' : 'deactivated'}`, 'success');
      fetchAccounts();
    } catch (error) {
      console.error('Error toggling account status:', error);
      showToast('Failed to update account status', 'error');
    }
  };

  const getAccountTypeIcon = (type) => {
    const icons = {
      checking: 'fa-university',
      savings: 'fa-piggy-bank',
      credit_card: 'fa-credit-card',
      cash: 'fa-money-bill-wave'
    };
    return icons[type] || 'fa-university';
  };

  return (
    <div className="modal-overlay" onClick={onClose}>
      <div className="modal-container" onClick={(e) => e.stopPropagation()} style={{ maxWidth: '800px' }}>
        <div className="modal-header">
          <h2>
            <i className="fas fa-university" style={{ marginRight: '12px', color: 'var(--primary-color)' }}></i>
            Manage Accounts
          </h2>
          <button className="modal-close-btn" onClick={onClose}>
            <i className="fas fa-times"></i>
          </button>
        </div>

        <div className="modal-body">
          {!showAddForm ? (
            <>
              <div style={{ marginBottom: '16px' }}>
                <button className="btn btn-primary" onClick={handleAddAccount}>
                  <i className="fas fa-plus"></i>
                  Add Account
                </button>
              </div>

              {loading ? (
                <div style={{ textAlign: 'center', padding: '40px' }}>
                  <div className="spinner-border text-primary"></div>
                </div>
              ) : (
                accounts.filter(a =>
                  !debts.some(d => d.id === a.id) &&
                  (a.type === 'checking' || a.type === 'savings')
                ).length === 0 ? (
                  <div className="empty-state">
                    <i className="fas fa-university empty-state-icon"></i>
                    <h3 className="empty-state-title">No bank accounts yet</h3>
                    <p className="empty-state-description">Add your first checking or savings account to start tracking transactions</p>
                  </div>
                ) : (
                  <div style={{ maxHeight: '400px', overflowY: 'auto' }}>
                    {accounts
                      .filter(a =>
                        !debts.some(d => d.id === a.id) &&
                        (a.type === 'checking' || a.type === 'savings')
                      )
                      .map(account => (
                        <div
                          key={account.id}
                          style={{
                            background: 'var(--bg-secondary)',
                            border: '1px solid var(--border-color)',
                            borderRadius: 'var(--border-radius)',
                            padding: '16px',
                            marginBottom: '12px',
                            opacity: account.is_active ? 1 : 0.6
                          }}
                        >
                          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'start' }}>
                            <div style={{ flex: 1 }}>
                              <div style={{ display: 'flex', alignItems: 'center', gap: '8px', marginBottom: '8px' }}>
                                <i className={`fas ${getAccountTypeIcon(account.type)}`} style={{ color: 'var(--primary-color)' }}></i>
                                <strong style={{ fontSize: '15px' }}>{account.name}</strong>
                                {!account.is_active && (
                                  <span style={{
                                    fontSize: '10px',
                                    padding: '2px 6px',
                                    borderRadius: '4px',
                                    background: 'var(--text-secondary)',
                                    color: 'white',
                                    fontWeight: 600
                                  }}>
                                    INACTIVE
                                  </span>
                                )}
                              </div>
                              <div style={{ fontSize: '12px', color: 'var(--text-secondary)', marginBottom: '4px' }}>
                                Type: {account.type.replace('_', ' ')}
                                {account.institution && ` • ${account.institution}`}
                                {account.account_number_last4 && ` • ****${account.account_number_last4}`}
                              </div>
                              <div style={{ fontSize: '14px', fontWeight: 600, color: 'var(--text-primary)', marginTop: '8px' }}>
                                Balance: {formatCurrency(account.current_balance || 0)}
                              </div>
                              {account.notes && (
                                <div style={{ fontSize: '11px', color: 'var(--text-secondary)', marginTop: '6px', fontStyle: 'italic' }}>
                                  {account.notes}
                                </div>
                              )}
                            </div>
                            <div style={{ display: 'flex', gap: '8px' }}>
                              <button
                                className="btn-table-action"
                                onClick={() => handleToggleActive(account)}
                                title={account.is_active ? 'Deactivate' : 'Activate'}
                              >
                                <i className={`fas ${account.is_active ? 'fa-eye-slash' : 'fa-eye'}`}></i>
                              </button>
                              <button
                                className="btn-table-action"
                                onClick={() => handleEditAccount(account)}
                                title="Edit account"
                              >
                                <i className="fas fa-edit"></i>
                              </button>
                              <button
                                className="btn-table-action danger"
                                onClick={() => handleDeleteAccount(account.id)}
                                title="Delete account"
                              >
                                <i className="fas fa-trash"></i>
                              </button>
                            </div>
                          </div>
                        </div>
                      ))}
                  </div>
                )
              )}
            </>
          ) : (
            <form onSubmit={handleSubmit}>
              <div className="form-group">
                <label className="form-label">Account Name *</label>
                <input
                  type="text"
                  name="name"
                  className="form-control"
                  value={formData.name}
                  onChange={handleChange}
                  placeholder="e.g., Chase Checking"
                  required
                />
              </div>

              <div className="form-group">
                <label className="form-label">Account Type *</label>
                <select
                  name="type"
                  className="form-control"
                  value={formData.type}
                  onChange={handleChange}
                  required
                  disabled={editingAccount && editingAccount.is_debt}
                >
                  <option value="checking">Checking</option>
                  <option value="savings">Savings</option>
                  <option value="cash">Cash</option>
                  <option value="investment">Investment</option>
                </select>
                <small style={{ color: 'var(--text-secondary)', fontSize: '11px', marginTop: '4px', display: 'block' }}>
                  Credit cards should be created under Debts
                </small>
              </div>

              <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '14px' }}>
                <div className="form-group">
                  <label className="form-label">Institution</label>
                  <input
                    type="text"
                    name="institution"
                    className="form-control"
                    value={formData.institution}
                    onChange={handleChange}
                    placeholder="Bank name"
                  />
                </div>

                <div className="form-group">
                  <label className="form-label">Last 4 Digits</label>
                  <input
                    type="text"
                    name="account_number_last4"
                    className="form-control"
                    value={formData.account_number_last4}
                    onChange={handleChange}
                    placeholder="1234"
                    maxLength="4"
                  />
                </div>
              </div>

              <div className="form-group">
                <label className="form-label">Current Balance</label>
                <input
                  type="number"
                  name="current_balance"
                  className="form-control"
                  value={formData.current_balance}
                  onChange={handleChange}
                  placeholder="0.00"
                  step="0.01"
                />
              </div>

              <div className="form-group">
                <label className="form-label">Notes</label>
                <textarea
                  name="notes"
                  className="form-control"
                  value={formData.notes}
                  onChange={handleChange}
                  placeholder="Optional notes about this account..."
                  rows="2"
                />
              </div>

              <div className="form-group">
                <label style={{ display: 'flex', alignItems: 'center', gap: '8px', cursor: 'pointer' }}>
                  <input
                    type="checkbox"
                    name="is_active"
                    checked={formData.is_active}
                    onChange={handleChange}
                    style={{ width: 'auto', cursor: 'pointer' }}
                  />
                  <span>Account is active</span>
                </label>
              </div>

              <div style={{ display: 'flex', gap: '12px', marginTop: '20px' }}>
                <button type="button" className="btn btn-secondary" onClick={() => setShowAddForm(false)}>
                  Cancel
                </button>
                <button type="submit" className="btn btn-primary">
                  <i className="fas fa-save"></i>
                  {editingAccount ? 'Update Account' : 'Add Account'}
                </button>
              </div>
            </form>
          )}
        </div>

        {!showAddForm && (
          <div className="modal-footer">
            <button className="btn btn-secondary" onClick={onSave}>
              Done
            </button>
          </div>
        )}
      </div>
    </div>
  );
};

export default ManageAccountsModal;
